import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Globe, Settings, LogOut, Layers,
  Key, CreditCard, FileText, Users, ShieldCheck,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import { usePlatformAdminStatus } from '@/hooks/usePlatformAdmin'
import { NotificationBell } from '@/components/notifications/NotificationBell'

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  author: 'Author',
  member: 'Member',
}

const ROLE_COLORS: Record<string, string> = {
  owner:  'bg-violet-100 text-violet-700',
  admin:  'bg-blue-100 text-blue-700',
  editor: 'bg-green-100 text-green-700',
  author: 'bg-amber-100 text-amber-700',
  member: 'bg-gray-100 text-gray-600',
}

export function Sidebar() {
  const { tenant, signOut, userRole } = useAuthStore()
  const perms = usePermissions()
  const { data: isPlatformAdmin } = usePlatformAdminStatus()

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Overview', show: true, end: true },
    { to: '/sites', icon: Globe, label: 'Sites', show: perms.canAccessSites },
    { to: '/blog', icon: FileText, label: 'Blog', show: perms.canAccessBlog },
    { to: '/team', icon: Users, label: 'Team', show: perms.canAccessTeam },
    { to: '/settings/api-keys', icon: Key, label: 'AI & API Keys', show: perms.canAccessSettings },
    { to: '/billing', icon: CreditCard, label: 'Billing', show: perms.canAccessBilling },
    { to: '/settings', icon: Settings, label: 'Settings', show: perms.canAccessSettings },
  ]

  return (
    <aside className="flex h-screen w-60 flex-col border-r border-gray-200 bg-white">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b border-gray-200 px-5">
        <Layers className="h-6 w-6 text-violet-600" />
        <span className="text-lg font-bold text-gray-900">Brix</span>
      </div>

      {/* Workspace + role */}
      {tenant && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="rounded-lg bg-violet-50 px-3 py-2">
            <p className="text-xs text-violet-500 font-medium uppercase tracking-wide">Workspace</p>
            <p className="text-sm font-semibold text-violet-900 truncate">{tenant.name}</p>
          </div>
          {userRole && (
            <div className="mt-2 flex items-center justify-end">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ROLE_COLORS[userRole] ?? ROLE_COLORS.member}`}>
                {ROLE_LABELS[userRole] ?? userRole}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.filter(i => i.show).map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-violet-50 text-violet-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              ].join(' ')
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Platform admin — cross-tenant, only visible to the founder */}
      {isPlatformAdmin && (
        <div className="border-t border-gray-200 px-3 py-2">
          <NavLink
            to="/platform-admin"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50"
          >
            <ShieldCheck className="h-4 w-4" />
            Platform Admin
          </NavLink>
        </div>
      )}

      {/* Notification bell */}
      <div className="border-t border-gray-200 px-3 py-2">
        <NotificationBell />
      </div>

      {/* Sign out */}
      <div className="border-t border-gray-200 p-3">
        <button
          onClick={() => void signOut()}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  )
}
